// src/contexts/financeiro/DashFinanceiroContext.js
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useFilial } from '../administrativo/FilialContext';
import { useSSE } from '../geral/SSEContext';
import { buscarDashFinanceiro } from '../../services/financeiro/DashFinanceiroService';
import { periodoFinanceiroPadrao, validarPeriodoFinanceiro } from '../../models/financeiro/DashFinanceiro';

const DashFinanceiroContext = createContext(null);
export const useDashFinanceiro = () => useContext(DashFinanceiroContext);

export const DashFinanceiroProvider = ({ children }) => {
  const { filialLogin } = useFilial();
  const { subscribeToEvent } = useSSE();
  const filialId = Number(filialLogin?.id);
  const [filtros, setFiltros] = useState(periodoFinanceiroPadrao);
  const [periodo, setPeriodo] = useState(periodoFinanceiroPadrao);
  const [dashboard, setDashboard] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [erroFiltro, setErroFiltro] = useState('');
  const [atualizadoEm, setAtualizadoEm] = useState(null);
  const requisicao = useRef(0);

  const atualizar = useCallback(async () => {
    const id = ++requisicao.current;
    if (!(filialId > 0)) {
      setDashboard(null);
      setErro('Filial do login não identificada.');
      setCarregando(false);
      return;
    }
    setCarregando(true);
    setErro('');
    try {
      const resultado = await buscarDashFinanceiro(filialId, periodo);
      if (id !== requisicao.current) return;
      setDashboard(resultado);
      setAtualizadoEm(new Date());
    } catch (error) {
      if (id === requisicao.current) setErro(error?.message || 'Não foi possível carregar o dashboard financeiro.');
    } finally {
      if (id === requisicao.current) setCarregando(false);
    }
  }, [filialId, periodo]);

  useEffect(() => {
    setDashboard(null);
    setAtualizadoEm(null);
    atualizar();
    return () => { requisicao.current += 1; };
  }, [atualizar]);

  useEffect(() => {
    let timer;
    const unsubscribe = subscribeToEvent('financeiro-alterado', (evento) => {
      try {
        const dados = JSON.parse(evento.data);
        if (Number(dados.filialId) !== filialId) return;
        clearTimeout(timer);
        timer = setTimeout(atualizar, 400);
      } catch (_) { /* Ignora eventos sem o contrato esperado. */ }
    });
    const aoRetornar = () => {
      if (document.visibilityState === 'visible') atualizar();
    };
    document.addEventListener('visibilitychange', aoRetornar);
    return () => {
      unsubscribe();
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', aoRetornar);
    };
  }, [subscribeToEvent, filialId, atualizar]);

  const aplicarFiltros = () => {
    const mensagem = validarPeriodoFinanceiro(filtros);
    setErroFiltro(mensagem);
    if (!mensagem) setPeriodo({ ...filtros });
  };

  return <DashFinanceiroContext.Provider value={{ dashboard, filialLogin, filtros, setFiltros,
    carregando, erro, erroFiltro, atualizadoEm, atualizar, aplicarFiltros }}>
    {children}
  </DashFinanceiroContext.Provider>;
};
